import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useGame } from '../context/GameContext';
import { WordPosition } from '../types/game';
import WordCircle from './WordCircle';
import ConnectionLines from './ConnectionLines';
import ErrorCircles from './ErrorCircles';
import { safePreventDefault, getTouchEventOptions } from '../utils/browserCompat';

interface GameBoardProps {
  width: number;
  height: number;
}

const GameBoard: React.FC<GameBoardProps> = ({ width, height }) => {
  const { gameState, actions } = useGame();
  const [dragLine, setDragLine] = useState<{ start: WordPosition; end: WordPosition } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationRef = useRef<number | null>(null);
  
  const gameStateRef = useRef(gameState);
  const actionsRef = useRef(actions);
  const dimensionsRef = useRef({ width, height });
  
  gameStateRef.current = gameState;
  actionsRef.current = actions;
  dimensionsRef.current = { width, height };

  const calculateWordPositions = () => {
    if (gameState.words.length === 0) return {};

    const centerX = width / 2;
    const centerY = height / 2;
    
    const bubbleRadius = getClickableRadius();
    const isMobile = width < 768;
    const safeMargin = isMobile ? 40 : 80;
    const minSpacing = 20;
    const minCenterDistance = (bubbleRadius * 2) + minSpacing;

    const angleStep = (2 * Math.PI) / gameState.words.length;
    const availableWidth = width - (safeMargin * 2) - (bubbleRadius * 2);
    const availableHeight = height - (safeMargin * 2) - (bubbleRadius * 2);
    const initialRadius = Math.min(availableWidth, availableHeight) / 3;

    const positions: Record<string, WordPosition> = {};
    gameState.words.forEach((word, index) => {
      const angle = angleStep * index - Math.PI / 2;
      positions[word] = {
        x: centerX + initialRadius * Math.cos(angle),
        y: centerY + initialRadius * Math.sin(angle),
      };
    });

    return applyRepulsionPhysics(positions, bubbleRadius, minSpacing, safeMargin, centerX, centerY);
  };

  const applyRepulsionPhysics = (
    initialPositions: Record<string, WordPosition>, 
    bubbleRadius: number, 
    minSpacing: number, 
    safeMargin: number,
    centerX: number,
    centerY: number
  ): Record<string, WordPosition> => {
    const positions = { ...initialPositions };
    const words = Object.keys(positions);
    const minCenterDistance = (bubbleRadius * 2) + minSpacing;
    const maxIterations = 100;
    const repulsionStrength = 0.3;
    const boundaryForce = 0.8;
    const dampening = 0.85;
    
    const bounds = {
      left: bubbleRadius + safeMargin,
      right: width - bubbleRadius - safeMargin,
      top: bubbleRadius + safeMargin,
      bottom: height - bubbleRadius - safeMargin
    };

    for (let iteration = 0; iteration < maxIterations; iteration++) {
      const forces: Record<string, { x: number; y: number }> = {};
      
      words.forEach(word => {
        forces[word] = { x: 0, y: 0 };
      });

      for (let i = 0; i < words.length; i++) {
        for (let j = i + 1; j < words.length; j++) {
          const word1 = words[i];
          const word2 = words[j];
          const pos1 = positions[word1];
          const pos2 = positions[word2];

          const dx = pos1.x - pos2.x;
          const dy = pos1.y - pos2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < minCenterDistance && distance > 0) {
            const overlap = minCenterDistance - distance;
            const force = overlap * repulsionStrength;
            
            const nx = dx / distance;
            const ny = dy / distance;
            
            forces[word1].x += nx * force;
            forces[word1].y += ny * force;
            forces[word2].x -= nx * force;
            forces[word2].y -= ny * force;
          }
        }
      }

      words.forEach(word => {
        const pos = positions[word];
        
        if (pos.x < bounds.left) {
          forces[word].x += (bounds.left - pos.x) * boundaryForce;
        }
        if (pos.x > bounds.right) {
          forces[word].x += (bounds.right - pos.x) * boundaryForce;
        }
        if (pos.y < bounds.top) {
          forces[word].y += (bounds.top - pos.y) * boundaryForce;
        }
        if (pos.y > bounds.bottom) {
          forces[word].y += (bounds.bottom - pos.y) * boundaryForce;
        }
      });

      let maxMovement = 0;
      words.forEach(word => {
        const pos = positions[word];
        const force = forces[word];
        
        const newX = pos.x + (force.x * dampening);
        const newY = pos.y + (force.y * dampening);
        
        const clampedX = Math.max(bounds.left, Math.min(bounds.right, newX));
        const clampedY = Math.max(bounds.top, Math.min(bounds.bottom, newY));
        
        const movement = Math.sqrt((clampedX - pos.x) ** 2 + (clampedY - pos.y) ** 2);
        maxMovement = Math.max(maxMovement, movement);
        
        positions[word] = { x: clampedX, y: clampedY };
      });

      if (maxMovement < 0.5) {
        break;
      }
    }

    return positions;
  };

  const animateWordMovement = (startPositions: Record<string, WordPosition>, endPositions: Record<string, WordPosition>) => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    const startTime = Date.now();
    const duration = 500;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const easeInOut = (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      const easedProgress = easeInOut(progress);

      const interpolatedPositions: Record<string, WordPosition> = {};
      Object.keys(endPositions).forEach(word => {
        const start = startPositions[word] || endPositions[word];
        const end = endPositions[word];
        
        interpolatedPositions[word] = {
          x: start.x + (end.x - start.x) * easedProgress,
          y: start.y + (end.y - start.y) * easedProgress,
        };
      });

      actions.updateWordPositions(interpolatedPositions);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
      }
    };

    animate();
  };

  useEffect(() => {
    if (gameState.words.length > 0) {
      const newPositions = calculateWordPositions();
      const currentPositions = gameState.wordPositions;
      
      const hasChanged = Object.keys(newPositions).some(word => 
        !currentPositions[word] || 
        Math.abs(currentPositions[word].x - newPositions[word].x) > 1 ||
        Math.abs(currentPositions[word].y - newPositions[word].y) > 1
      );
      
      if (hasChanged || Object.keys(currentPositions).length === 0) {
        if (Object.keys(currentPositions).length > 0 && hasChanged) {
          animateWordMovement(currentPositions, newPositions);
        } else {
          actions.updateWordPositions(newPositions);
        }
      }
    }
  }, [gameState.words, width, height, actions, isAnimating]);

  useEffect(() => {
    if (!gameState.isDragging || !gameState.selectedWord) {
      setDragLine(null);
    }
  }, [gameState.isDragging, gameState.selectedWord]);

  useEffect(() => {
    setDragLine(null);
  }, [gameState.connections.length]);

  useEffect(() => {
    const jumpWordsStable = (word1: string, word2: string) => {
      const startTime = Date.now();
      const duration = 300;
      const currentHeight = dimensionsRef.current.height;
      const jumpHeight = currentHeight * 0.02;
      
      const originalPositions = {
        [word1]: { ...gameStateRef.current.wordPositions[word1] },
        [word2]: { ...gameStateRef.current.wordPositions[word2] }
      };

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        if (progress < 1) {
          const displacement = Math.sin(progress * Math.PI) * jumpHeight;
          
          const newPositions = {
            ...gameStateRef.current.wordPositions,
            [word1]: {
              x: originalPositions[word1].x,
              y: originalPositions[word1].y - displacement
            },
            [word2]: {
              x: originalPositions[word2].x,
              y: originalPositions[word2].y - displacement
            }
          };
          
          actionsRef.current.updateWordPositions(newPositions);
          requestAnimationFrame(animate);
        } else {
          actionsRef.current.updateWordPositions({
            ...gameStateRef.current.wordPositions,
            [word1]: originalPositions[word1],
            [word2]: originalPositions[word2]
          });
        }
      };

      animate();
    };

    const wiggleWordsStable = (word1: string, word2: string) => {
      const startTime = Date.now();
      const duration = 300;
      const currentWidth = dimensionsRef.current.width;
      const wiggleDistance = currentWidth * 0.005;
      
      const originalPositions = {
        [word1]: { ...gameStateRef.current.wordPositions[word1] },
        [word2]: { ...gameStateRef.current.wordPositions[word2] }
      };

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        if (progress < 1) {
          const displacement = Math.sin(progress * 4 * Math.PI) * wiggleDistance;
          
          const newPositions = {
            ...gameStateRef.current.wordPositions,
            [word1]: {
              x: originalPositions[word1].x + displacement,
              y: originalPositions[word1].y
            },
            [word2]: {
              x: originalPositions[word2].x + displacement,
              y: originalPositions[word2].y
            }
          };
          
          actionsRef.current.updateWordPositions(newPositions);
          requestAnimationFrame(animate);
        } else {
          actionsRef.current.updateWordPositions({
            ...gameStateRef.current.wordPositions,
            [word1]: originalPositions[word1],
            [word2]: originalPositions[word2]
          });
        }
      };

      animate();
    };

    actionsRef.current.setAnimationCallbacks(jumpWordsStable, wiggleWordsStable);
  }, []);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    if (gameState.isDragging && gameState.selectedWord && svgRef.current) {
      const rect = svgRef.current.getBoundingClientRect();
      const end = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      
      const start = gameState.wordPositions[gameState.selectedWord];
      if (start) {
        setDragLine({ start, end });
      }
    }
  };

  const handleMouseUp = (event: React.MouseEvent<SVGSVGElement>) => {
    if (gameState.isDragging) {
      actions.setDragging(false);
    }
    setDragLine(null);
  };

  const handleTouchMove = (event: React.TouchEvent<SVGSVGElement>) => {
    if (gameState.isDragging && gameState.selectedWord && svgRef.current && event.touches.length > 0) {
      try {
        safePreventDefault(event.nativeEvent);
        const rect = svgRef.current.getBoundingClientRect();
        const touch = event.touches[0];
        if (!touch) return;
      const touchX = touch.clientX - rect.left;
      const touchY = touch.clientY - rect.top;
      const end = { x: touchX, y: touchY };
      
      const start = gameState.wordPositions[gameState.selectedWord];
      if (start) {
        setDragLine({ start, end });
        
        let hoveredWord = null;
        let closestDistance = Infinity;
        const clickableRadius = getClickableRadius();
        
        for (const word of gameState.words) {
          const position = gameState.wordPositions[word];
          if (position && word !== gameState.selectedWord) {
            const distance = Math.sqrt(
              Math.pow(touchX - position.x, 2) + Math.pow(touchY - position.y, 2)
            );
            
            if (distance <= clickableRadius && distance < closestDistance && actions.isWordSelectable(word)) {
              hoveredWord = word;
              closestDistance = distance;
            }
          }
        }
        
        if (gameState.hoveredWord !== hoveredWord) {
          actions.setHoveredWord(hoveredWord);
        }
      }
      } catch (error) {
        console.warn('Touch move error:', error);
        actions.setDragging(false);
      }
    }
  };

  const handleTouchEnd = (event: React.TouchEvent<SVGSVGElement>) => {
    if (gameState.isDragging && gameState.selectedWord && svgRef.current) {
      try {
        const rect = svgRef.current.getBoundingClientRect();
        const touch = event.changedTouches[0];
        if (!touch) {
          actions.setDragging(false);
          return;
        }
        const touchX = touch.clientX - rect.left;
        const touchY = touch.clientY - rect.top;
      
      let targetWord = null;
      let closestDistance = Infinity;
      const clickableRadius = getClickableRadius();
      
      for (const word of gameState.words) {
        const position = gameState.wordPositions[word];
        if (position && word !== gameState.selectedWord) {
          const distance = Math.sqrt(
            Math.pow(touchX - position.x, 2) + Math.pow(touchY - position.y, 2)
          );
          
          if (distance <= clickableRadius && distance < closestDistance) {
            if (actions.isWordSelectable(word)) {
              targetWord = word;
              closestDistance = distance;
            }
          }
        }
      }
      
      
      if (targetWord) {
        actions.validateConnection(gameState.selectedWord, targetWord);
      }
      
      actions.setDragging(false);
      } catch (error) {
        console.warn('Touch end error:', error);
        actions.setDragging(false);
      }
    } else if (gameState.isDragging) {
      actions.setDragging(false);
    }
    
    setDragLine(null);
    if (gameState.hoveredWord) {
      actions.setHoveredWord(null);
    }
  };

  const getClickableRadius = () => {
    const isMobile = width < 768;
    const safeMargin = isMobile ? 40 : 80;
    const availableWidth = width - (safeMargin * 2);
    const availableHeight = height - (safeMargin * 2);
    const minDimension = Math.min(availableWidth, availableHeight);
    const baseRadius = Math.max(minDimension * (isMobile ? 0.45 : 0.35), isMobile ? 160 : 150);
    const radius = Math.min(baseRadius, Math.min(width, height) * (isMobile ? 0.5 : 0.4));
    
    const angleStep = (2 * Math.PI) / gameState.words.length;
    const spacingFactor = isMobile ? 0.65 : 0.8;
    const maxRadius = radius * Math.sin(angleStep / 2) * spacingFactor;
    
    const minRadius = Math.max(isMobile ? 35 : 20, minDimension * (isMobile ? 0.06 : 0.03));
    const maxAllowedRadius = Math.min(isMobile ? 65 : 50, minDimension * (isMobile ? 0.12 : 0.08));
    
    return Math.max(minRadius, Math.min(maxRadius, maxAllowedRadius));
  };

  return (
    <motion.svg
      ref={svgRef}
      width={width}
      height={height}
      className="game-board"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <defs>
        <radialGradient id="backgroundGradient" cx="50%" cy="50%" r="70%">
          <stop offset="0%" stopColor="#f8fafc" />
          <stop offset="50%" stopColor="#f1f5f9" />
          <stop offset="100%" stopColor="#e2e8f0" />
        </radialGradient>
        
        <linearGradient id="defaultGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#f8fafc" />
        </linearGradient>
        
        <linearGradient id="selectedGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#1d4ed8" />
        </linearGradient>
        
        <linearGradient id="selectedGlow" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#60a5fa" />
          <stop offset="100%" stopColor="#3b82f6" />
        </linearGradient>
        
        <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#10b981" />
          <stop offset="100%" stopColor="#059669" />
        </linearGradient>
        
        <pattern id="dotPattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
          <circle cx="20" cy="20" r="1" fill="#e2e8f0" opacity="0.3" />
        </pattern>
      </defs>
      
      <rect width={width} height={height} fill="url(#backgroundGradient)" />
      <rect width={width} height={height} fill="url(#dotPattern)" />
      
      <motion.circle
        cx={width * 0.15}
        cy={height * 0.2}
        r={60}
        fill="rgba(59, 130, 246, 0.05)"
        animate={{
          y: [0, -20, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.circle
        cx={width * 0.85}
        cy={height * 0.8}
        r={80}
        fill="rgba(168, 85, 247, 0.05)"
        animate={{
          y: [0, 15, 0],
          scale: [1, 0.9, 1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2,
        }}
      />
      <motion.circle
        cx={width * 0.7}
        cy={height * 0.15}
        r={40}
        fill="rgba(16, 185, 129, 0.05)"
        animate={{
          y: [0, -10, 0],
          x: [0, 10, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 4,
        }}
      />

      <ErrorCircles centerX={width / 2} centerY={height / 2} errors={gameState.errors} />

      <ConnectionLines connections={gameState.connections} wordPositions={gameState.wordPositions} />

      {dragLine && (
        <line
          x1={dragLine.start.x}
          y1={dragLine.start.y}
          x2={dragLine.end.x}
          y2={dragLine.end.y}
          stroke="#94a3b8"
          strokeWidth={3}
          strokeDasharray="5,5"
          opacity={0.7}
        />
      )}

      {gameState.words.map((word) => {
        const position = gameState.wordPositions[word];
        if (!position) return null;

        const clickableRadius = getClickableRadius();
        const baseFontSize = Math.max(clickableRadius * 0.4, 10);
        const scaledFontSize = Math.min(baseFontSize * gameState.fontSizeScale, clickableRadius * 0.8);

        return (
          <WordCircle
            key={word}
            word={word}
            position={position}
            radius={clickableRadius}
            fontSize={scaledFontSize}
            isSelected={gameState.selectedWord === word}
          />
        );
      })}
    </motion.svg>
  );
};

export default GameBoard;